import {
	Box,
	IconButton,
	Input,
	inputClasses,
	Sheet,
	Table,
	Typography,
	useTheme
} from '@mui/joy';
import { DatatableHeader, HeadCell, Order } from './DatatableHeader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DatatableFooter } from './DatatableFooter';
import {
	KeyboardArrowLeft,
	KeyboardArrowRight,
	Search
} from '@mui/icons-material';
import { useMediaQuery } from '../../hooks';
import { useEventListener } from '../../hooks/useEventListener';
import { useIsMounted } from '../../hooks/useIsMounted';
import { SCRAMBLE_APPENDIX_ENABLED } from '../../lib/const';

export interface DatatableProps<T extends Record<string, string | number>> {
	rows: T[];
	headCells: HeadCell<T>[];
	idKey: keyof T;
	searchable?: boolean;
	searchPlaceholder?: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator<Key extends string | number | symbol>(
	order: Order,
	orderBy: Key
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string }
) => number {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

const DatatableCell = ({
	value,
	numeric,
	numericFormat
}: {
	value: string | number;
	numeric?: boolean;
	numericFormat?: 'currency' | 'percent';
}) => {
	const formatNumber = () => {
		if (numericFormat === 'currency') {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD'
			}).format(value as number);
		}
		if (numericFormat === 'percent') {
			return new Intl.NumberFormat('en-US', {
				style: 'percent',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(value as number);
		}
		return value;
	};

	return (
		<td style={{ textAlign: numeric ? 'right' : 'left' }}>
			<Typography level='chartFilter'>
				{numeric ? formatNumber() : value}
			</Typography>
		</td>
	);
};

export const Datatable = <T extends { [key in keyof T]: string | number }>({
	rows,
	headCells,
	idKey,
	searchable = false,
	searchPlaceholder = 'Search'
}: DatatableProps<T>) => {
	const encoder = useMemo(
		() => ({
			A: 'T',
			B: 'C',
			C: 'Y',
			D: 'S',
			E: 'A',
			F: 'M',
			G: 'F',
			H: 'I',
			I: 'G',
			J: 'H',
			K: 'V',
			L: 'U',
			M: 'J',
			N: 'E',
			O: 'Z',
			P: 'R',
			Q: 'B',
			R: 'Q',
			S: 'X',
			T: 'L',
			U: 'P',
			V: 'K',
			W: 'N',
			X: 'D',
			Y: 'W',
			Z: 'O',
			a: 'p',
			b: 's',
			c: 'b',
			d: 'k',
			e: 'c',
			f: 'f',
			g: 'j',
			h: 'w',
			i: 'x',
			j: 'e',
			k: 'o',
			l: 'v',
			m: 'q',
			n: 'a',
			o: 'l',
			p: 'n',
			q: 'r',
			r: 'g',
			s: 'h',
			t: 'y',
			u: 'z',
			v: 't',
			w: 'm',
			x: 'd',
			y: 'u',
			z: 'i'
		}),
		[]
	);
	const encodeDecode = useCallback(
		(input: string) => {
			const encodedArray: string[] = [];
			for (let i = 0; i < input.length; i++) {
				const currentLetter = input[i]; // Convert to lowercase for case-insensitivity

				// Check if the current letter is in the map
				if (encoder[currentLetter as keyof typeof encoder]) {
					// If yes, use the mapped letter
					encodedArray.push(encoder[currentLetter as keyof typeof encoder]);
				} else {
					// If not, keep the original letter
					encodedArray.push(input[i]);
				}
			}

			return encodedArray.join('');
		},
		[encoder]
	);
	const encodedRows = useMemo(
		() =>
			rows.map((row) => ({
				...row,
				[idKey]:
					typeof row[idKey] === 'string'
						? SCRAMBLE_APPENDIX_ENABLED
							? encodeDecode(row[idKey])
							: row[idKey]
						: row[idKey]
			})),
		[rows, encodeDecode, idKey]
	);
	const [order, setOrder] = useState<Order>('asc');
	const [orderBy, setOrderBy] = useState<keyof T>(idKey);
	const [filteredRows, setFilteredRows] = useState(encodedRows);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	const handleRequestSort = (property: keyof T) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setFilteredRows(encodedRows);
		handleChangePage(0);
	}, [encodedRows]);

	const handleChangeRowsPerPage = (newValue: number | null) => {
		setRowsPerPage(parseInt(newValue!.toString(), 10));
		setPage(0);
	};

	const handleSearch = (searchValue: string) => {
		const searchWords = searchValue.split(' ');
		const newfilteredRows = encodedRows.filter((row) => {
			return searchWords
				.filter((word) => word !== '')
				.every((word) => {
					const value = row[idKey].toString();
					return value.toLowerCase().includes(word.toLowerCase());
				});
		});
		setFilteredRows(newfilteredRows);
	};

	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

	const tableRef = useRef<HTMLDivElement | null>(null);
	const [isAtTableLeft, setIsAtTableLeft] = useState(true);
	const [isAtTableRight, setIsAtTableRight] = useState(false);

	useEffect(() => {
		tableRef.current?.scrollTo(0, 0);
	}, [rows]);

	const tableScrollLeft = () => {
		tableRef.current?.scrollBy({
			top: 0,
			left: tableRef.current ? tableRef.current.clientWidth * -0.45 : -256,
			behavior: 'smooth'
		});
	};

	const tableScrollRight = () => {
		tableRef.current?.scrollBy({
			top: 0,
			left: tableRef.current ? tableRef.current.clientWidth * 0.45 : 256,
			behavior: 'smooth'
		});
	};

	const theme = useTheme();
	const matchesMd = useMediaQuery(theme.breakpoints.up('md'));
	const [tableLeft, setTableLeft] = useState(0);
	const onResize = () => {
		if (tableRef.current) {
			const rect = tableRef.current.getBoundingClientRect();
			setTableLeft(rect.left);
		}
	};

	const isMounted = useIsMounted();

	useEffect(() => {
		if (isMounted()) {
			onResize();
		}
	}, [isMounted]);
	useEventListener('resize', onResize);

	const onTableScroll = () => {
		if (tableRef.current) {
			setIsAtTableLeft(tableRef.current.scrollLeft === 0);
			setIsAtTableRight(
				tableRef.current.scrollWidth - tableRef.current.clientWidth <=
					tableRef.current.scrollLeft
			);
		}
	};
	useEventListener('scroll', () => {});

	return (
		<Box
			style={{
				width: matchesMd ? `calc(100vw - ${tableLeft}px)` : '100%'
			}}
			sx={{
				height: '100%',
				position: 'relative',
				overflowX: 'auto',
				marginLeft: 0,
				marginRight: 'auto'
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: { xs: '50px', md: '150px', lg: '250px' }, // Adjust the width of the overlay as needed
					background: `linear-gradient(to right, #FFFFFF00, #FAFAFA, #FAFAFA)`, // Adjust the gradient as needed
					pointerEvents: 'none',
					zIndex: 50
				}}
			></Box>
			<Box
				sx={{
					width: '100%',
					background: (theme) => theme.vars.palette.background.surface,
					pt: '1.5em',
					pb: '2.5em',
					position: 'sticky',
					bottom: 0,
					right: 0
				}}
			>
				{searchable && (
					<Input
						aria-label={`${searchPlaceholder}`}
						onChange={(e) => handleSearch(e.target.value)}
						placeholder={searchPlaceholder}
						sx={{
							mb: '1em',
							width: '100%',
							maxWidth: '271px',
							mt: '1.5em',
							height: '50px',
							boxShadow: '0px 1px 4px #00000029',
							border: '1px solid #D1D5DB',

							[`&.${inputClasses.colorNeutral}`]: {
								':hover': { color: '#374151' }
							}
						}}
						endDecorator={<Search />}
					/>
				)}
			</Box>
			{!isAtTableLeft && (
				<IconButton
					sx={{
						position: 'absolute',
						left: 15,
						zIndex: 10,
						borderRadius: '50%',
						top: searchable ? 110 : 25,
						border: '1px solid #D1D5DB',
						backgroundColor: '#FFFFFF',
						boxShadow: '0px 1px 4px #00000029'
					}}
					aria-label='scroll table left'
					onClick={() => tableScrollLeft()}
				>
					<KeyboardArrowLeft />
				</IconButton>
			)}
			{!isAtTableRight && (
				<IconButton
					sx={{
						position: 'absolute',
						right: { xs: 45, md: 150, lg: 250 },
						zIndex: 10,
						borderRadius: '50%',
						top: searchable ? 110 : 25,
						border: '1px solid #D1D5DB',
						backgroundColor: '#FFFFFF',
						boxShadow: '0px 1px 4px #00000029'
					}}
					aria-label='scroll table right'
					onClick={() => tableScrollRight()}
				>
					<KeyboardArrowRight />
				</IconButton>
			)}
			<Sheet
				onScroll={() => onTableScroll()}
				variant='solid'
				sx={{
					position: 'relative',
					// the number is the amount of the header rows.
					'--TableHeader-height': 'calc(1 * var(--TableCell-height))',
					// background needs to have transparency to show the scrolling shadows
					'--TableRow-stripeBackground': 'rgba(0 0 0 / 0.04)',
					'--TableRow-hoverBackground': 'rgba(0 0 0 / 0.08)',
					overflow: 'auto',
					backgroundSize:
						'40px calc(100% - var(--TableCell-height)), 40px calc(100% - var(--TableCell-height)), 14px calc(100% - var(--TableCell-height)), 14px calc(100% - var(--TableCell-height))',
					backgroundRepeat: 'no-repeat',
					backgroundAttachment: 'local, local, scroll, scroll',
					backgroundPosition:
						'var(--Table-firstColumnWidth) var(--TableCell-height), calc(100% - var(--Table-lastColumnWidth)) var(--TableCell-height), var(--Table-firstColumnWidth) var(--TableCell-height), calc(100% - var(--Table-lastColumnWidth)) var(--TableCell-height)',
					backgroundColor: (theme) => theme.vars.palette.background.surface
				}}
				ref={tableRef}
			>
				<Table
					sx={{
						paddingRight: { xs: '50px', md: '150px', lg: '250px' },
						'--TableCell-headBackground': (theme) =>
							theme.vars.palette.controls.controlFill,
						'--TableCell-selectedBackground': (theme) =>
							theme.vars.palette.success.softBg,
						'& tr': {
							backgroundColor: 'white'
						},
						'& tbody td': {
							maxWidth: '5px'
						},

						'& tr>td': {
							borderBottom: '1px solid #DADADA',
							paddingY: '14px'
						},
						'thead th:not([colspan])': {
							borderBottom: '1px solid #DADADA'
						},
						'& tr > th:first-of-type': {
							position: 'sticky',
							left: 0,
							zIndex: 5,
							backgroundColor: 'white',
							fontWeight: 'semi-bold',
							borderBottom: '1px solid #DADADA'
						},

						'& thead tr th:first-of-type': {
							width: 200,
							textWrap: 'wrap',
							whiteSpace: 'normal',
							position: 'sticky',
							left: 0,
							zIndex: 5,
							backgroundColor: (theme) =>
								theme.vars.palette.controls.controlFill,
							borderBottom: '1px solid #DADADA'
						},
						'& th:nth-child(even)': {
							backgroundColor: (theme) =>
								theme.vars.palette.controls.controlFill
						},
						'& tfoot': {
							position: 'sticky',
							left: 0,
							bottom: 0,
							zIndex: 1,
							backgroundColor: 'var(--TableCell-headBackground)',
							boxShadow: '1px 0 var(--TableCell-borderColor)'
						}
					}}
				>
					<DatatableHeader
						headCells={headCells}
						order={order}
						orderBy={String(orderBy)}
						onRequestSort={handleRequestSort}
					/>
					<tbody>
						{filteredRows
							.slice()
							.sort(getComparator(order, orderBy))
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((row, index) => {
								const labelId = `datatable-table-${index}`;

								return (
									<tr tabIndex={-1} key={row[idKey]}>
										<th
											id={labelId}
											scope='row'
											style={{ textWrap: 'wrap', whiteSpace: 'normal' }}
										>
											<Typography level='chartFilter'>{row[idKey]}</Typography>
										</th>
										{headCells
											.map((hc) => hc.id)
											.filter((key) => key !== idKey)
											.map((key) => (
												<DatatableCell
													key={`${key as string}`}
													value={row[key]}
													numeric={
														headCells.find((hc) => hc.id === key)?.numeric
													}
													numericFormat={
														headCells.find((hc) => hc.id === key)?.numericFormat
													}
												/>
											))}
									</tr>
								);
							})}
						{emptyRows > 0 && (
							<tr
								style={
									{
										height: `calc(${emptyRows} * 40px)`
									} as React.CSSProperties
								}
							>
								<td colSpan={headCells.length} aria-hidden />
							</tr>
						)}
					</tbody>
				</Table>
			</Sheet>
			{/* Content of the sticky footer */}
			<DatatableFooter
				rows={rows}
				page={page}
				rowsPerPage={rowsPerPage}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleChangePage={handleChangePage}
			/>
		</Box>
	);
};
